import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import "dayjs/locale/es"; // Importa el locale español
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { DayCalendarSkeleton } from "@mui/x-date-pickers/DayCalendarSkeleton";
import { Badge } from "@mui/joy";
import { Box } from "@mui/material";

// Configura dayjs para usar el locale español
dayjs.locale("es");

function CustomDay(props) {
  const { day, outsideCurrentMonth, highlightedDates, ...other } = props;

  const turno = highlightedDates.find((turno) =>
    dayjs(turno.date).isSame(day, "day"),
  );
  const isToday = dayjs().isSame(day, "day");

  return (
    <Badge
      key={day.toString()}
      variant="solid"
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      badgeContent={
        turno
          ? turno.cantTurnos > 9
            ? "+9"
            : "" + turno.cantTurnos
          : undefined
      }
      sx={{ fontSize: "8px" }}
      size="md"
      badgeInset="10%"
      invisible={!turno}
    >
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
        sx={{
          ...(turno && {
            color: "white",
            background: `linear-gradient(135deg, ${turno?.backgroundColor || "#667eea"} 0%, ${turno?.backgroundColor || "#764ba2"}dd 100%)`,
            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
            "&:hover": {
              transform: "scale(1.1)",
              boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
            },
          }),
          ...(!turno && {
            color: "#6b7280",
            backgroundColor: isToday ? "#fff7ed" : "#f8fafc",
            border: isToday ? "2px solid #f59e0b" : "1px solid #e5e7eb",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
            "&.Mui-disabled": {
              color: "#9ca3af",
              backgroundColor: isToday ? "#fff7ed" : "#f9fafb",
              borderColor: isToday ? "#f59e0b" : "#e5e7eb",
            },
          }),
          borderRadius: "10px",
          fontSize: "16px",
          fontWeight: 600,
          margin: "2px",
          transition: "all 0.3s ease",
          "&.Mui-selected": {
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            boxShadow: "0 6px 20px rgba(102, 126, 234, 0.5)",
            transform: "scale(1.15)",
            "&:hover": {
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            },
          },
        }}
        disabled={!turno}
      />
    </Badge>
  );
}

export default function CustomCalendar({
  value,
  onChange,
  minDate,
  maxDate,
  highlightedDates,
}) {
  const [calendarValue, setCalendarValue] = useState(
    value ? dayjs(value) : null,
  );
  const lastExternalValue = useRef(value);

  useEffect(() => {
    if (value !== lastExternalValue.current) {
      lastExternalValue.current = value;
      setCalendarValue(value ? dayjs(value) : null);
    }
  }, [value]);

  const handleChange = (newValue) => {
    setCalendarValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleMonthChange = (newMonth) => {
    setCalendarValue((prev) => {
      if (prev && dayjs.isDayjs(prev)) {
        const day = prev.date();
        return newMonth.date(day);
      }
      return newMonth;
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "0",
          background: "transparent",
          "& .MuiDateCalendar-root": {
            background: "white",
            borderRadius: "16px",
            padding: "16px",
            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.1)",
            border: "2px solid #667eea",
            width: "100%",
            maxWidth: "400px",
          },
          "& .MuiDayCalendar-root": {
            minHeight: "300px",
          },
          "& .MuiDayCalendar-slideTransition": {
            minHeight: "300px !important",
          },
          "& .MuiPickersCalendarHeader-root": {
            paddingLeft: "8px",
            paddingRight: "8px",
            marginBottom: "12px",
          },
          "& .MuiPickersCalendarHeader-label": {
            fontSize: "16px",
            fontWeight: 700,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            textTransform: "capitalize",
          },
          "& .MuiPickersArrowSwitcher-button": {
            color: "#667eea",
            "&:hover": {
              background: "rgba(102, 126, 234, 0.1)",
            },
          },
        }}
      >
        <DateCalendar
          value={calendarValue}
          onChange={handleChange}
          onMonthChange={handleMonthChange}
          renderLoading={() => <DayCalendarSkeleton />}
          slots={{
            day: CustomDay,
          }}
          slotProps={{
            day: {
              highlightedDates,
            },
          }}
          showDaysOutsideCurrentMonth
          minDate={minDate}
          maxDate={maxDate}
          disablePast
          dayOfWeekFormatter={(weekday) => (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: "11px",
                fontWeight: 700,
                color: "#4c1d95",
                textTransform: "uppercase",
                backgroundColor: "#f3f4f6",
                borderRadius: "8px",
                padding: "4px 8px",
              }}
            >
              {weekday.format("dd")}
            </Box>
          )}
        />
      </Box>
    </LocalizationProvider>
  );
}
